/**
 * Paying a debt out as a strategy: which debts are cleared, from which assets, and what the balance
 * sheet looks like afterwards.
 *
 * WHY THIS IS A STRATEGY AND NOT BOOKKEEPING. An unsecured debt does not reduce assessable assets, but
 * the cash sitting against it does. So clearing a $50,000 personal loan with $50,000 of cash leaves net
 * worth unmoved and takes $50,000 straight out of the assets test — worth several thousand a year of
 * Age Pension, plus the interest no longer paid, for a client who looks no poorer on paper. A secured
 * mortgage is a different case again: one over an investment property already nets off that property's
 * assessed value, so clearing it moves the means test barely at all, while one over an EXEMPT family
 * home is doing nothing for the means test and is a straight win to clear. None of that can be decided
 * by a rule of thumb — it has to be modelled both ways and compared, which is what this makes possible.
 *
 * IT DRAWS ON THE SAME ASSETS AS THE RAD, deliberately. A dollar can pay a deposit or retire a debt, not
 * both, and the whole advice is in that competition. So `funding` is the same shape the RAD allocator
 * uses and is liquidated by the same {@link liquidateRadFundingAssets}: one arithmetic, one clamp, one
 * CGT treatment. The UI is responsible for not allocating the same dollar twice; the engine clamps per
 * asset regardless and says so when it raised less than was asked for.
 *
 * CLEARING A DEBT OUTRIGHT needs no help from this module: the loan-repayment plugin reads each
 * liability's balance live and stops servicing one that reaches zero, so the instalments stop from the
 * next period on, at the client's own cadence.
 *
 * A PARTIAL PAYOFF has to decide what happens to the repayment, and {@link DebtPayoff.repaymentBasis}
 * is that decision. Keeping the instalment (the default) retires the smaller balance sooner and saves
 * interest; lowering it frees cashflow now and costs more interest over the life of the loan. Neither
 * wins universally — a client paying aged care fees may need the cashflow more than the saving — so
 * both are modelled and the suggestion engine ranks them. Until this option existed the product always
 * lowered the repayment, not by decision but because auto-calculate re-derived the instalment from the
 * reduced balance and nobody had considered the case.
 *
 * WHEN IT HAPPENS is the caller's choice, exactly as with the RAD: `debtPayoffPlugin` performs it on a
 * date inside the projection, while {@link applyDebtPayoffToFactFind} performs it at the origin for
 * callers that bake strategies into a fact-find before projecting (the suggestion engine, and the
 * frontend's before/after simulation).
 */

import { liquidateRadFundingAssets, type RadFunding } from './radFunding.ts';
import { calculateLoanRepayment } from '../taxAndLoanUtils.ts';
import type { FactFind, FactFindLiability } from '../factFindTypes.ts';

/**
 * A debt-payoff strategy.
 *
 * `payments` is keyed `"<category>::<liabilityId>"` — see {@link debtPayoffKey}. A double colon rather
 * than the RAD allocator's single hyphen because a liability id is a UUID and a category key is
 * free-form (the Support at Home engine injects its own `loans` category), so a single-character
 * separator that also appears inside UUIDs could not be split back apart unambiguously.
 */
export interface DebtPayoff {
    /** How much of each debt to clear. Clamped to the outstanding balance at the moment it is applied. */
    payments?: Record<string, number>;
    /** Assets sold to fund it — the same shape, allocator and CGT treatment as `strategyOptions.radFunding`. */
    funding?: RadFunding;
    /**
     * When it happens, `YYYY-MM-DD`. Absent means "at the projection's origin". The plugin defaults it
     * to `radFundingDate` so that, by default, one sale funds both the deposit and the payoff on one day.
     */
    date?: string;
    /**
     * After a PARTIAL payoff, what happens to the repayment — the question every lender asks and the
     * product previously answered by accident.
     *
     * `'keep_repayment'` (the default) holds the instalment where it is, so the smaller balance is
     * retired sooner and less interest is paid overall. This is what Australian lenders do unless the
     * borrower asks otherwise, and it is the more common real-world outcome.
     *
     * `'lower_repayment'` re-derives a smaller instalment over the remaining term, freeing cashflow now
     * and paying more interest across the life of the loan.
     *
     * Neither is universally better, which is why both are modelled: a client paying aged care fees may
     * need the cashflow more than the interest saving. It only matters for a partial payoff — a debt
     * cleared outright has no repayment left to decide about.
     */
    repaymentBasis?: 'keep_repayment' | 'lower_repayment';
}

/** The key a payment is stored under: category and liability id, joined unambiguously. */
export function debtPayoffKey(category: string, liabilityId: string | number): string {
    return `${category}::${liabilityId}`;
}

/** Splits a {@link debtPayoffKey} back apart, or null when it is not one. */
export function parseDebtPayoffKey(key: string): { category: string; liabilityId: string } | null {
    const separator = key.indexOf('::');
    if (separator <= 0) return null;
    const category = key.slice(0, separator);
    const liabilityId = key.slice(separator + 2);
    return liabilityId ? { category, liabilityId } : null;
}

/** One debt this strategy pays, located in the fact-find so the balance can be reduced in place. */
export interface DebtPayoffTarget {
    key: string;
    category: string;
    index: number;
    liability: FactFindLiability;
    /** The outstanding balance at the moment of resolution. */
    outstanding: number;
    /** What will actually come off it — the amount asked for, clamped to what is owed. */
    amount: number;
}

/**
 * The debts a payoff names, located in `factFind` and clamped to what is actually owed.
 *
 * Clamped because a debt shrinks between the day an adviser allocates against it and the day the payoff
 * happens — every repayment in between reduces it. Paying more than is owed would hand the lender money
 * the client never owed and, worse, drive the balance negative, where it would read as an asset.
 *
 * Resolved against the LIVE fact-find rather than the one the allocation was made on, for the same
 * reason the RAD funding sale prices itself against live balances: what is there is what can be paid.
 */
export function resolveDebtPayoffTargets(factFind: FactFind, payoff: DebtPayoff | null | undefined): DebtPayoffTarget[] {
    const payments = payoff?.payments;
    if (!payments) return [];

    const targets: DebtPayoffTarget[] = [];
    for (const [key, requested] of Object.entries(payments)) {
        const parsed = parseDebtPayoffKey(key);
        if (!parsed) continue;
        const asked = Math.max(0, Number(requested) || 0);
        if (asked <= 0) continue;

        const list = factFind.liabilities?.[parsed.category];
        if (!list) continue;
        const index = list.findIndex((item) => String(item?.id ?? '') === parsed.liabilityId);
        if (index < 0) continue;

        const liability = list[index]!;
        const outstanding = Math.max(0, Number(liability.balance) || 0);
        const amount = Math.min(asked, outstanding);
        if (amount <= 0) continue;

        targets.push({ key, category: parsed.category, index, liability, outstanding, amount });
    }
    return targets;
}

/** What a payoff would cost in total, against the balances as they stand. */
export function totalDebtPayoffAmount(factFind: FactFind, payoff: DebtPayoff | null | undefined): number {
    return resolveDebtPayoffTargets(factFind, payoff).reduce((sum, target) => sum + target.amount, 0);
}

/**
 * Holds a partly-repaid debt's instalment where it was, so the smaller balance clears SOONER rather
 * than the repayment falling.
 *
 * The mechanism is deliberately one the engine already has rather than a new rule: the liability is
 * switched to a MANUAL repayment carrying the instalment it was being paid at. Every later year then
 * reads that fixed figure instead of re-deriving a smaller one from the reduced balance, and the
 * repayment plugin's existing cap — an instalment is never larger than what is still owed plus that
 * period's interest — lands the final payment exactly on zero and stops. Nothing else has to know the
 * option exists.
 *
 * `outstandingBefore` is the balance as it stood BEFORE this payoff, because that is what the client's
 * current instalment was derived from. Passed in rather than read off the liability, which the caller
 * is about to reduce.
 *
 * Two cases are left alone. A debt cleared outright has no instalment to freeze, and writing one would
 * leave a repayment schedule attached to a zero balance. A debt already on a MANUAL repayment is
 * already frozen — its instalment never re-derived in the first place — so there is nothing to do.
 */
function freezeRepaymentAtCurrentInstalment(liability: FactFindLiability, outstandingBefore: number, remaining: number): void {
    if (remaining <= 0) return;
    if (liability.repaymentCalculation !== 'auto') return;

    const rate = Number(liability.interestRate) || 0;
    const term = Number(liability.loanTerm) || 0;
    // Without a rate and a term there was never a derived instalment to hold on to; the engine would
    // have been using `repayments` already, which is frozen by definition.
    if (outstandingBefore <= 0 || rate <= 0 || term <= 0) return;

    const instalment = calculateLoanRepayment(
        outstandingBefore,
        rate,
        term,
        String(liability.frequency || 'monthly'),
        String(liability.loanType || 'pi'),
    );
    if (!(instalment > 0)) return;

    liability.repaymentCalculation = 'manual';
    liability.repayments = instalment;
}

/** One debt's payoff, as it happened — what a narrative or a card needs to describe it. */
export interface DebtPaidOff {
    description: string;
    paid: number;
    /** What remains owing afterwards. Zero when the debt was retired outright. */
    remaining: number;
    clearedInFull: boolean;
}

/**
 * Reduces the nominated debts IN PLACE, returning what was paid off each.
 *
 * The money to do it is NOT taken here — the caller decides where it comes from, because that differs
 * by context: the plugin withdraws it from the bank on a date, while the origin-time helper below has
 * already sold the funding assets into it. Splitting the two is what lets both share this clamp.
 */
export function payOffDebtsInFactFind(factFind: FactFind, payoff: DebtPayoff | null | undefined): DebtPaidOff[] {
    // Defaulting to keeping the repayment because that is what lenders do unless asked otherwise. The
    // product used to lower it — not by decision, but because auto-calculate re-derived the instalment
    // from the smaller balance and nobody had thought about the case.
    const keepsRepayment = (payoff?.repaymentBasis ?? 'keep_repayment') === 'keep_repayment';
    const paidOff: DebtPaidOff[] = [];
    for (const target of resolveDebtPayoffTargets(factFind, payoff)) {
        const remaining = target.outstanding - target.amount;
        if (keepsRepayment) freezeRepaymentAtCurrentInstalment(target.liability, target.outstanding, remaining);
        target.liability.balance = remaining;
        paidOff.push({
            description: String(target.liability.description || 'debt'),
            paid: target.amount,
            remaining,
            // Rounded, so a debt left owing a fraction of a cent by floating-point arithmetic still
            // reads — and is described — as cleared.
            clearedInFull: Math.round(remaining) <= 0,
        });
    }
    return paidOff;
}

/** What {@link applyDebtPayoffToFactFind} did, for a caller that needs to describe or verify it. */
export interface DebtPayoffOutcome {
    factFind: FactFind;
    /** Net proceeds raised by selling the nominated funding assets, after any CGT. */
    proceeds: number;
    /** What was actually paid off, debt by debt. */
    paidOff: DebtPaidOff[];
    /** Total cleared across every debt. */
    totalPaid: number;
}

/**
 * Moves `amount` into the client's primary bank account (negative to take it out), creating one if
 * there is none.
 *
 * A deliberately small mirror of the engine's own `routeSurplusToBank`, which lives in a server plugin
 * this package cannot reach. Kept to the same choice of account — the first listed — so the origin-time
 * path and the dated one put the money in the same place.
 */
function moveThroughPrimaryBank(factFind: FactFind, amount: number): void {
    if (amount === 0) return;
    const bankAccounts = factFind.assets?.bankAccounts;
    if (bankAccounts && bankAccounts.length > 0) {
        bankAccounts[0]!.value = (Number(bankAccounts[0]!.value) || 0) + amount;
        return;
    }
    if (!factFind.assets) factFind.assets = {};
    if (!factFind.assets.bankAccounts) factFind.assets.bankAccounts = [];
    factFind.assets.bankAccounts.push({
        id: 'auto-debt-payoff',
        type: 'bankAccounts',
        description: 'Surplus Cash',
        value: amount,
        income: 0,
        incomeRate: 2,
        growthRate: 0,
        ownership: 'client',
    });
}

/**
 * Applies a debt payoff at the projection's ORIGIN, returning a new fact-find — the caller's is never
 * touched.
 *
 * For callers that bake strategies into a fact-find before projecting: the suggestion engine, and the
 * frontend's before/after simulation (whose "after" position must show the debt gone, or the Financial
 * Impact panel would show the client a normalised year still servicing a debt the plan retires).
 *
 * The money TRANSITS THE BANK rather than passing straight from the sold assets to the lender, and that
 * matters. Selling $50,000 of shares does not raise $50,000 — CGT comes out first — so paying $50,000
 * off a debt with the proceeds would invent the difference. Routing both sides through the bank means
 * any gap lands where a gap belongs: as less cash. It also makes funding the payoff FROM the bank work
 * without a special case (the account is debited by the sale and credited by the proceeds, a wash,
 * then debited again by the payment).
 */
export function applyDebtPayoffToFactFind(
    factFind: FactFind,
    payoff: DebtPayoff | null | undefined,
    cgtOverrides: Record<string, unknown> = {},
): DebtPayoffOutcome {
    const modified = structuredClone(factFind);
    if (!payoff) return { factFind: modified, proceeds: 0, paidOff: [], totalPaid: 0 };

    // Priced against the caller's original, which `structuredClone` has just detached — the same rule
    // the RAD sale follows, so two liquidations in one pass are each priced off what the client really
    // held rather than off what the earlier one left behind.
    const proceeds = liquidateRadFundingAssets(modified, payoff.funding, cgtOverrides, factFind);
    moveThroughPrimaryBank(modified, proceeds);

    const paidOff = payOffDebtsInFactFind(modified, payoff);
    const totalPaid = paidOff.reduce((sum, row) => sum + row.paid, 0);
    moveThroughPrimaryBank(modified, -totalPaid);

    return { factFind: modified, proceeds, paidOff, totalPaid };
}
